import { useEffect, memo, useState, useCallback } from 'react'
import { observer } from 'mobx-react'
import { useInView } from 'react-intersection-observer'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Grid from '@mui/material/Grid'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import { LoadingMode } from 'components/LoadingProvider'
import HotlineFallback from 'components/HotlineFallback'
import { useErrorState } from 'context/Error/hook'
import CategoryLayout from 'components/Layout/Category'
import PreviousPageErrors from 'components/PageErrors/PreviousPageErrors'
import useStore from 'stores/utils/hooks/useStore'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useDebouncedEffect } from 'hooks/useDebouncedEffect'
import { Button } from '@mui/material'
import { useAsyncEffect } from 'hooks/useAsyncEffect'
import { NavigateFunction } from 'react-router-dom'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { CONFIG_DDF_SMAVA, configMap, CONFIG_DDF } from 'config/utils/config'
import { trackTeal } from 'utils/tracking'
import { AFFILIATE_ID_SMAVA } from 'utils/affiliate'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { PageText } from 'components/RceEdit/RCEEdit'
import useTenant from 'hooks/useTenant'
import { formNavigationEditPageStyles } from './FormNavigationEditPage.styles'

type Props = {
  onNext: () => void
  navigate: NavigateFunction
}

const FormNavigationEditPage = observer(({ navigate, onNext }: Props) => {
  const { isSmava } = useTenant()
  const { rootStore, loadingMode } = useStore((store) => ({
    rootStore: store,
    loadingMode: store.loadingMode
  }))
  const {
    isInIframe,
    page: { setConfiguration },
    traversal: {
      data: {
        debtors: {
          primary: { personId }
        },
        system: { subId, refId, configurationId, advertisementId }
      }
    },
    traversalSession: { setDacWidgetState }
  } = rootStore

  const {
    state: { message },
    hasCooldown
  } = useErrorState()

  const { ref, inView } = useInView()

  const onDataChanged = async () => {
    trackInAmplitude(AmplitudeEvent.RceEditUpdateDataClicked)
    trackTeal({
      transaction: {
        event: 'rce_edit_data_clicked'
      },
      affiliateId: AFFILIATE_ID_SMAVA,
      subId,
      refId,
      configurationId,
      traversalPersonId: personId,
      context: {
        Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
        userAttributes: {
          advertisementId
        }
      }
    })
    await setConfiguration(
      isSmava
        ? (await configMap[CONFIG_DDF_SMAVA]()).default
        : (await configMap[CONFIG_DDF]()).default
    )
    setDacWidgetState('initial')
    rootStore.history.replace('base')
    navigate('base')
  }

  useDebouncedEffect(
    () => {
      if (inView) {
        trackInAmplitude(AmplitudeEvent.CtaIsVisible)
      }
    },
    300,
    [inView]
  )

  const {
    page: { getCurrentIndex, scrollToFirstError },
    navigation: { currentPageInSession },
    fieldErrors,
    traversal: { acceptTermsAndConditions }
  } = rootStore
  const continueButtonText =
    PageText[currentPageInSession as keyof typeof PageText]

  const currentIndex = getCurrentIndex()

  const scrollToError = useCallback(
    () => scrollToFirstError(isInIframe),
    [isInIframe, scrollToFirstError]
  )

  const [hasErrorsOnCurrentPage, setHasErrorsOnCurrentPage] = useState(false)
  const [previousErrorPageName, setPreviousErrorPageName] = useState<string>()

  const handleSubmitButtonClick = async () => {
    acceptTermsAndConditions()
    onNext()
  }

  useAsyncEffect(async () => {
    if (!currentPageInSession) {
      return
    }

    const errorsOnCurrentPage =
      await fieldErrors.getErrors(currentPageInSession)
    const hasErrorsOnCurrentPageFlag =
      Object.keys(errorsOnCurrentPage).length > 0

    setHasErrorsOnCurrentPage(hasErrorsOnCurrentPageFlag)

    const previousErrorPageNameFlag =
      await fieldErrors.findFirstErrorPage(currentIndex)

    setPreviousErrorPageName(previousErrorPageNameFlag)
  }, [currentPageInSession, loadingMode])

  useEffect(() => {
    if (loadingMode === LoadingMode.None && hasErrorsOnCurrentPage) {
      scrollToError()
    }
  }, [hasErrorsOnCurrentPage, loadingMode, scrollToError])

  const { flexBasisGrid, button, buttonFullHeight } = useEmotionStyles(
    formNavigationEditPageStyles,
    'primary'
  )
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (
    previousErrorPageName &&
    !hasErrorsOnCurrentPage &&
    currentPageInSession !== 'editPage_additional'
  ) {
    return (
      <PreviousPageErrors
        configToNavigate={isSmava ? CONFIG_DDF_SMAVA : CONFIG_DDF}
        navigate={navigate}
        pageName={previousErrorPageName}
      />
    )
  }

  return (
    <CategoryLayout ref={ref}>
      {message && <HotlineFallback message={message} />}
      <Grid
        container
        spacing={1}
        direction={isMobile ? 'column' : 'row-reverse'}
        justifyContent="center"
        alignItems="center"
      >
        <Grid
          className={flexBasisGrid}
          container
          item
          spacing={1}
          direction={isMobile ? 'column' : 'row-reverse'}
          xs={12}
        >
          <Grid item xs={12}>
            <Button
              className={button}
              size="large"
              fullWidth
              id="next-submit"
              name="next-submit"
              variant="contained"
              color={'primary'}
              endIcon={
                loadingMode === LoadingMode.None ? (
                  <ArrowForwardIcon />
                ) : undefined
              }
              disabled={hasCooldown}
              onClick={handleSubmitButtonClick}
            >
              {continueButtonText}
            </Button>
          </Grid>

          <Grid
            item
            xs={12}
            data-testid="grid-item-next-submit-container"
            display="flex"
            alignItems="center"
            fontWeight={700}
          >
            <Button
              className={buttonFullHeight}
              size="large"
              fullWidth
              variant={'outlined'}
              color="primary"
              disabled={hasCooldown}
              onClick={onDataChanged}
            >
              Weitere Angaben ändern
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </CategoryLayout>
  )
})

export default memo(FormNavigationEditPage)
